/**
 * Resolve a stored asset URL to something an <img> can actually load (EIP-561).
 *
 * Uploaded tenant logos are stored as a BACKEND-relative path,
 * `/api/uploads/tenants/{id}/{file}` (backend/routers/tenant.py), and are served
 * only by the backend service (backend/routers/assets.py). Frontend and backend
 * are separate origins in Cloud Run, and the frontend's nginx has no /api proxy,
 * so an <img src="/api/..."> resolves against the frontend origin and 404s.
 * Locally it works anyway, because CRA's dev proxy forwards /api — which is why
 * this only ever showed up on deployed environments.
 *
 * The prefix is deliberately scoped to `/api/` rather than "anything not
 * absolute". `frontend/public/aureon-logo.png` still exists, so the bogus
 * `/aureon-logo.png` an old seeder wrote (EIP-481) resolves correctly today
 * against the frontend origin; a blanket rule would take a working image and
 * point it at a backend that has never heard of it.
 */

/** Backend origin, or '' when unset (local dev, where CRA proxies /api). */
function backendOrigin() {
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
}

/**
 * @param {string|null|undefined} url a stored logo/icon URL
 * @returns the URL an <img src> can use. Falsy input is returned unchanged so
 *          callers can keep using `url ? <img/> : <initials/>`.
 */
export function resolveAssetUrl(url) {
  if (!url || typeof url !== 'string') return url;

  // Absolute (http://, https://, data:, blob:) and protocol-relative URLs are
  // already loadable — Brandfetch logos arrive this way.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) return url;

  // Backend-served assets, and only these, need the origin.
  if (url.startsWith('/api/')) return `${backendOrigin()}${url}`;

  return url;
}

export default resolveAssetUrl;
